import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Check, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money, dateTime } from "@/lib/format";
import { PageTitle, StatusBadge, EmptyState } from "@/components/brand";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const UpiSettings = () => {
  const [f, setF] = useState({ upi_id: "", payee_name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/upi").then((r) => r.data.upi_id && setF({ upi_id: r.data.upi_id, payee_name: r.data.payee_name })); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/upi", f); toast.success("UPI details saved"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="upi-settings" className="card-gold mb-8 grid gap-4 p-6 md:grid-cols-[1fr_1fr_auto] md:items-end">
      <div><label htmlFor="upi-id" className="text-sm font-semibold">Your UPI ID (users pay here)</label>
        <input id="upi-id" data-testid="upi-id-input" value={f.upi_id} onChange={(e) => setF({ ...f, upi_id: e.target.value.trim() })} placeholder="name@bank" className="input-cr mt-2" /></div>
      <div><label htmlFor="upi-name" className="text-sm font-semibold">Payee name</label>
        <input id="upi-name" data-testid="upi-name-input" value={f.payee_name} onChange={(e) => setF({ ...f, payee_name: e.target.value })} placeholder="Name shown in UPI app" className="input-cr mt-2" /></div>
      <button data-testid="upi-save-btn" disabled={busy || !f.upi_id.includes("@") || f.payee_name.length < 2} onClick={save} className="btn-gold px-6 py-3 normal-case">{busy ? "Saving…" : "Save UPI"}</button>
    </div>
  );
};

const ProofDialog = ({ tx, onClose, onDone }) => {
  const [img, setImg] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (tx) { setImg(null); setReason(""); api.get(`/admin/upi-deposits/${tx.id}/proof`).then((r) => setImg(r.data.image)); } }, [tx]);
  const decide = async (decision) => {
    setBusy(true);
    try { await api.post(`/admin/upi-deposits/${tx.id}/decision`, { decision, reason }); toast.success(decision === "approve" ? "Approved, coins credited" : "Rejected"); window.dispatchEvent(new Event("admin-coins-changed")); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="upi-proof-dialog" className="max-h-[90vh] overflow-y-auto border-cr-line bg-cr-surface text-cr-ink">
        <DialogTitle>Payment {tx?.utr}</DialogTitle>
        {tx && <div className="space-y-3 text-sm">
          <p>{tx.user_email} · <b className="text-cr-gold">{money(tx.amount, { whole: true })}</b> → {tx.coins} coins · paid to {tx.paid_to}</p>
          {img ? <img data-testid="upi-proof-image" src={img} alt="Payment proof" className="max-h-[50vh] w-full rounded-lg object-contain" /> : <p className="text-cr-muted">Loading screenshot…</p>}
          {tx.status === "pending" ? <>
            <input data-testid="upi-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Note (optional, shown to user if rejected)" className="input-cr" />
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="upi-approve-btn" disabled={busy} onClick={() => decide("approve")} className="btn-gold py-3 normal-case"><Check className="h-4 w-4" />Approve</button>
              <button data-testid="upi-reject-btn" disabled={busy} onClick={() => decide("reject")} className="btn-outline py-3 normal-case text-cr-danger"><X className="h-4 w-4" />Reject</button>
            </div></> : <p>Status: <StatusBadge status={tx.status} /> {tx.reviewed_by && `by ${tx.reviewed_by}`}</p>}
        </div>}
      </DialogContent>
    </Dialog>
  );
};

const Withdrawals = () => {
  const [rows, setRows] = useState(null);
  const [utr, setUtr] = useState({});
  const load = () => api.get("/admin/coin-withdrawals", { params: { status: "all" } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const decide = async (t, decision) => {
    try { await api.post(`/admin/coin-withdrawals/${t.id}/decision`, { decision, payout_utr: utr[t.id] || "" }); toast.success(decision === "approve" ? "Marked paid" : "Rejected, coins returned"); window.dispatchEvent(new Event("admin-coins-changed")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (rows && rows.length === 0) return <EmptyState title="No withdrawal requests" text="When users withdraw coins to UPI they appear here." />;
  return (
    <div className="card-cr overflow-x-auto">
      <table data-testid="withdrawals-table" className="w-full min-w-[900px] text-sm">
        <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["User", "Amount", "Pay to UPI", "Status", "Requested", "Payout UTR / action"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>{(rows || []).map((t) => (
          <tr key={t.id} data-testid={`withdraw-admin-row-${t.id}`} className="border-t border-cr-line">
            <td className="px-4 py-3">{t.user_email}</td><td className="px-4 py-3 font-semibold text-cr-gold">{money(t.coins, { whole: true })}</td>
            <td className="px-4 py-3 font-mono text-xs">{t.payout_upi}</td><td className="px-4 py-3"><StatusBadge status={t.status} /></td>
            <td className="px-4 py-3 text-cr-muted">{dateTime(t.created_at)}</td>
            <td className="px-4 py-3">{t.status === "pending" ? (
              <div className="flex items-center gap-2">
                <input data-testid={`withdraw-utr-${t.id}`} value={utr[t.id] || ""} onChange={(e) => setUtr({ ...utr, [t.id]: e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() })} placeholder="UTR after paying" className="input-cr py-1.5 text-xs" />
                <button data-testid={`withdraw-paid-${t.id}`} disabled={(utr[t.id] || "").length < 6} onClick={() => decide(t, "approve")} className="btn-gold px-3 py-1.5 text-xs normal-case">Paid</button>
                <button data-testid={`withdraw-reject-${t.id}`} onClick={() => decide(t, "reject")} className="btn-outline px-3 py-1.5 text-xs normal-case text-cr-danger">Reject</button>
              </div>) : <span className="font-mono text-xs">{t.payout_utr || "-"}</span>}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
};

export default function AdminPayments() {
  const { user } = useAuth();
  const [tab, setTab] = useState("deposits");
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/upi-deposits", { params: { status } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="admin-payments-page">
      <PageTitle crumbs={["Admin", "UPI Payments"]} title="UPI Payments" subtitle="Add-coin payments: check UTR + screenshot, approving moves coins from you to the user. Withdrawals: pay the user by UPI, enter the UTR, coins come back to you." />
      {user?.role === "admin" && <UpiSettings />}
      <div className="mb-6 flex gap-6 border-b border-cr-line">{[["deposits", "Add-coin payments"], ["withdrawals", "Withdrawals"]].map(([k, l]) => (
        <button key={k} data-testid={`payments-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 pb-3 text-sm font-semibold ${tab === k ? "border-cr-gold text-cr-gold" : "border-transparent text-cr-ink/75"}`}>{l}</button>))}
      </div>
      {tab === "withdrawals" ? <Withdrawals /> : <>
      <div className="mb-4 flex gap-2">{["pending", "completed", "rejected", "all"].map((s) => (
        <button key={s} data-testid={`upi-filter-${s}`} onClick={() => setStatus(s)} className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${status === s ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line"}`}>{s}</button>))}
      </div>
      {rows && rows.length === 0 ? <EmptyState title="No payments" text="New UPI payments from your users appear here." /> : (
        <div className="card-cr overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["User", "Amount", "UTR", "Status", "Submitted", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{(rows || []).map((t) => (
              <tr key={t.id} data-testid={`upi-row-${t.id}`} className="border-t border-cr-line">
                <td className="px-4 py-3">{t.user_email}</td><td className="px-4 py-3 font-semibold text-cr-gold">{money(t.amount, { whole: true })}</td>
                <td className="px-4 py-3 font-mono text-xs">{t.utr}</td><td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-3 text-cr-muted">{dateTime(t.created_at)}</td>
                <td className="px-4 py-3 text-right"><button data-testid={`upi-review-${t.id}`} onClick={() => setOpen(t)} className="btn-outline px-3 py-1.5 text-xs normal-case"><Eye className="h-4 w-4" />Review</button></td>
              </tr>))}</tbody>
          </table>
        </div>)}
      </>}
      <ProofDialog tx={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />
    </div>
  );
}
